import React, { useEffect, useState } from "react";
import styles from "./BlogView.module.css";
import { Container, Modal, Button, Spinner } from "react-bootstrap";
import { FaArrowLeft, FaShareAlt } from "react-icons/fa";
import moment from "moment";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AxiosInstance } from "../../axios";
import { Helmet } from "react-helmet-async";
import SeoPage from "../../components/seo/SeoPage";

/* Helper - extract a YouTube embed URL from any supported input
   (watch / share / shorts / embed / iframe code). Returns "" when
   the input isn't a recognizable YouTube reference. */
const getYoutubeEmbedUrl = (input) => {
  if (!input) return "";
  const raw = String(input);
  const iframeMatch = raw.match(/<iframe[^>]+src=["']([^"']+)["']/i);
  const url = iframeMatch ? iframeMatch[1] : raw.trim();
  const idMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/i,
  );
  if (idMatch && idMatch[1]) {
    return "https://www.youtube.com/embed/" + idMatch[1];
  }
  return "";
};

const BlogView = ({ showHeaderIcons = true }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: blogIdFromParam, slug } = useParams();

  // Try both: from location state or fallback to URL param
  const blogId = location.state?.blogId || blogIdFromParam;
  const selectedCategory = location.state?.selectedCategory || "";
  const selectedTag = location.state?.selectedTag || "";

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareLink = `${window.location.origin}/api/api/blog/${blogId}`;
  const baseURL = AxiosInstance.defaults.baseURL;

  const handleCopy = () => {
    try {
      const tempInput = document.createElement("textarea");
      tempInput.value = shareLink;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  useEffect(() => {
    if (!blogId) {
      setError("No blog ID provided to view.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    AxiosInstance.post("/api/blog/view", { id: blogId })
      .then((res) => {
        if (res.data.status) {
          setBlog(res.data.blog);
        } else {
          setError("Blog post not found.");
          setBlog(null);
        }
      })
      .catch((err) => {
        console.error("Error fetching blog:", err);
        setError("Failed to load blog post. Please try again.");
        setBlog(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [blogId]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        {/* <div className="text-center">
          <Spinner animation="border" variant="primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading blog post...</p>
        </div> */}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <div className="text-center text-danger">
          <h4>Oops!</h4>
          <p>{error}</p>
          <Button
            variant="primary"
            onClick={() => navigate("/blog")}
            className="mt-3"
          >
            Back to Blogs
          </Button>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <div className="text-center">
          <h4>Blog Not Found</h4>
          <p>The blog post you're looking for doesn't exist.</p>
          <Button
            variant="primary"
            onClick={() => navigate("/blog")}
            className="mt-3"
          >
            Back to Blogs
          </Button>
        </div>
      </div>
    );
  }

  let fullCoverImageUrl = "";

  const base = AxiosInstance.defaults.baseURL;

  if (blog?.cover_image_path) {
    if (
      blog.cover_image_path?.startsWith("blob:") ||
      blog.cover_image_path?.startsWith("http://") ||
      blog.cover_image_path?.startsWith("https://")
    ) {
      fullCoverImageUrl = blog?.cover_image_path;
    } else {
      fullCoverImageUrl = `${base.replace(/\/+$/, "")}/api/${blog.cover_image_path.replace(/^\/+/, "")}`;
    }
  } else {
    fullCoverImageUrl =
      "https://placehold.co/1200x600/E0E0E0/333333?text=No+Image";
  }

  /* Cover media priority (matches the HTML template):
       - If youtube_url resolves to a valid YouTube id -> render iframe.
       - Otherwise -> render the cover image (existing behavior). */
  const coverEmbedUrl = getYoutubeEmbedUrl(blog?.youtube_url);

  // Helper function to strip HTML for meta description
  const stripHtml = (htmlString) => {
    if (!htmlString) return "";
    return htmlString.replace(/<[^>]*>/g, ""); // Strip HTML tags
  };

  const metaDescription =
    blog?.tagline ||
    stripHtml(blog?.content).substring(0, 160) +
      (stripHtml(blog?.content).length > 160 ? "..." : "");

  return (
    <>
      <SeoPage title={blog?.title} description={blog?.tagline || metaDescription} />
      <Container fluid className={`p-0 ${styles.blogWrapper}`}>
        <div className={styles.headerSection}>
          <div className={`${styles.headerTop} d-flex align-items-center`}>
            {showHeaderIcons && (
              <FaArrowLeft
                className={styles.backIcon}
                onClick={() =>
                  navigate("/blog", {
                    state: {
                      selectedCategory,
                      selectedTag,
                    },
                  })
                }
                style={{ cursor: "pointer" }}
                title="Go back"
              />
            )}
            <h5 className={styles.headerTitle}>{blog?.title}</h5>
          </div>

          <div className={styles.subtitleSection}>
            <p className={`${styles.subtitleText} ${styles.labelText}`}>
              {blog?.tagline}
            </p>
          </div>

          <div
            className={`m-0 ${styles.metaBar} d-flex justify-content-between align-items-center flex-wrap`}
          >
            <div>
              <p className={`m-0 ${styles.labelText}`}>
                <strong>By:</strong> {blog?.author_name}, {blog?.author_designation}
              </p>
            </div>
            <div>
              <p className={`m-0 ${styles.labelText}`}>
                <strong>Published:</strong> {moment(blog?.posted_date).format("MMMM D, YYYY")}
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              {showHeaderIcons && (
                <div className="d-flex align-items-center gap-2">
                  <FaShareAlt
                    className={styles.shareIcon}
                    title="Share this blog"
                    style={{ cursor: "pointer", fontSize: "18px" }}
                    onClick={() => setShowShareModal(true)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.paddingX}>
          {/* Cover media - YouTube video takes priority over the cover image */}
          <div className={`my-4 ${styles.coverMediaContainer}`}>
            {coverEmbedUrl ? (
              <div className={styles.coverVideoWrapper}>
                <iframe
                  className={styles.coverVideo}
                  src={coverEmbedUrl}
                  title={blog?.title || "Blog Video"}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            ) : (
              <img
                src={fullCoverImageUrl}
                alt={blog?.title || "Blog Cover"}
                className={styles.coverImage}
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://placehold.co/1200x600/E0E0E0/333333?text=Image+Not+Found";
                }}
              />
            )}
          </div>

          <article
            className={`ql-editor ${styles.contentText}`}
            dangerouslySetInnerHTML={{ __html: blog?.content }}
          />

          <div className={styles.categoryTagWrapper}>
            <div className="row py-3 g-3">
              <div className="col-md-6">
                <div className={styles.labelText}>Category</div>
                <div className={styles.valueText}>
                  {blog?.categorie_name || "N/A"}
                </div>
              </div>
              <div className="col-md-6">
                <div className={styles.labelText}>Tags</div>
                <div className={styles.valueText}>
                  {blog?.tag?.map((t) => t.tag_name).join(", ") || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Share Modal */}
        <Modal
          show={showShareModal}
          onHide={() => setShowShareModal(false)}
          centered
          size="md"
        >
          <Modal.Header closeButton>
            <Modal.Title>Share This Blog</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex flex-column align-items-start w-100">
              <p className="text-muted mb-3">
                Copy the link below to share this blog post:
              </p>
              <div className="position-relative w-100">
                <input
                  type="text"
                  className="form-control pe-5 rounded-3"
                  value={shareLink}
                  readOnly
                  style={{
                    backgroundColor: "#f8f9fa",
                    borderColor: "#dee2e6",
                    padding: "12px",
                    fontSize: "14px"
                  }}
                />
                <button
                  className="btn btn-link position-absolute"
                  onClick={handleCopy}
                  style={{
                    top: "50%",
                    right: "5px",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    color: "#142454",
                    fontSize: "1rem",
                    textDecoration: "none",
                    padding: "8px"
                  }}
                  title="Copy to clipboard"
                >
                  {copied ? "✓ Copied!" : "Copy"}
                </button>
              </div>

              {copied && (
                <div
                  className="text-success mt-3 w-100 text-center"
                  style={{ fontSize: "14px", fontWeight: "500" }}
                >
                  Link copied to clipboard!
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowShareModal(false);
                setCopied(false);
              }}
              className="rounded-3"
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default BlogView;